import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiJson } from '@/lib/api';
import type {
  AdminDripCatalogEntry,
  AdminDripMetricsResponse,
  AdminDripSendsResponse,
} from '@/lib/types';

const CONV_PER_SEND_TOOLTIP =
  'Conversions ÷ sends in the selected range. Directional yield; conversions and sends use different grain — see catalog notes.';

type Props = {
  campaignId: string;
  catalogEntry: AdminDripCatalogEntry | undefined;
  range: '7d' | '30d' | '90d' | 'all';
};

function pct(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${((100 * part) / whole).toFixed(1)}%`;
}

/** Sum sends per calendar day for a simple bar chart */
function dailyTotalsByDate(daily: AdminDripMetricsResponse['daily_sends']): { date: string; sends: number }[] {
  const m = new Map<string, number>();
  for (const row of daily) {
    const d = row.date.slice(0, 10);
    m.set(d, (m.get(d) ?? 0) + row.sends);
  }
  return [...m.entries()]
    .map(([date, sends]) => ({ date, sends }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function CampaignDetailDrawer({ campaignId, catalogEntry, range }: Props) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ from: '/admin/drip-campaigns' });

  const metricsQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'metrics', campaignId, range],
    queryFn: () =>
      apiJson<AdminDripMetricsResponse>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/metrics?range=${range}`
      ),
    enabled: !!campaignId,
  });

  const sendsQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'sends', campaignId, range],
    queryFn: () =>
      apiJson<AdminDripSendsResponse>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/sends?limit=50&offset=0`
      ),
    enabled: !!campaignId,
  });

  const eligQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'eligibility', campaignId],
    queryFn: () =>
      apiJson<{ id: string; eligible_now: number | null; description: string }>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/eligibility`
      ),
    enabled: !!campaignId,
  });

  const series = useMemo(
    () => dailyTotalsByDate(metricsQuery.data?.daily_sends ?? []),
    [metricsQuery.data?.daily_sends]
  );
  const maxS = useMemo(() => Math.max(1, ...series.map((s) => s.sends)), [series]);

  const close = () => {
    navigate({
      to: pathname,
      search: { ...search, campaign: undefined },
    });
  };

  const m = metricsQuery.data;
  const c = catalogEntry;

  return (
    <div
      className='fixed inset-0 z-50 flex justify-end bg-black/30'
      role='presentation'
      onClick={close}
    >
      <div
        className='h-full w-full max-w-2xl overflow-y-auto bg-white shadow-xl border-l border-gray-200 p-6'
        role='dialog'
        aria-modal
        aria-labelledby='drip-drawer-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h2 id='drip-drawer-title' className='text-xl font-semibold text-gray-900'>
              {c?.name ?? campaignId}
            </h2>
            <p className='text-xs font-mono text-gray-500 mt-1'>{campaignId}</p>
          </div>
          <button
            type='button'
            onClick={close}
            className='rounded-md px-3 py-1.5 text-sm text-gray-600 border border-gray-300 hover:bg-gray-50'
          >
            Close
          </button>
        </div>

        {c && (
          <div className='mt-4 flex flex-wrap gap-2 text-xs'>
            <span className='rounded-full bg-gray-100 px-2 py-0.5 capitalize'>{c.goal}</span>
            {c.channels.map((ch) => (
              <span key={ch} className='rounded-full bg-sky-50 text-sky-800 px-2 py-0.5'>
                {ch}
              </span>
            ))}
            {!c.enabled && (
              <span className='rounded-full bg-amber-100 text-amber-900 px-2 py-0.5'>Disabled</span>
            )}
            {!c.tracking_enabled && (
              <span
                className='rounded-full bg-slate-100 text-slate-800 px-2 py-0.5'
                title='Sent with mailgun_tracking=False for deliverability — opens are not recorded in DB.'
              >
                Tracking off
              </span>
            )}
          </div>
        )}

        {c?.notes && <p className='mt-3 text-sm text-gray-600'>{c.notes}</p>}

        <div className='mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm'>
          <span className='font-semibold text-gray-800'>Eligible now: </span>
          {eligQuery.isLoading ? (
            '…'
          ) : eligQuery.data?.eligible_now == null ? (
            '—'
          ) : (
            <span className='tabular-nums font-semibold'>{eligQuery.data.eligible_now}</span>
          )}
          {eligQuery.data?.description && (
            <p className='text-xs text-gray-600 mt-1'>{eligQuery.data.description}</p>
          )}
        </div>

        {metricsQuery.isLoading ? (
          <div className='flex justify-center py-12 mt-6'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : metricsQuery.isError ? (
          <p className='mt-6 text-red-600'>Could not load metrics.</p>
        ) : m ? (
          <>
            <div className='mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center'>
              <div className='rounded-lg border border-gray-200 bg-white p-3'>
                <p className='text-[10px] font-semibold uppercase text-gray-500'>Sends</p>
                <p className='text-lg font-semibold tabular-nums'>{m.total_sends}</p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-white p-3'>
                <p className='text-[10px] font-semibold uppercase text-gray-500'>Opens</p>
                <p className='text-lg font-semibold tabular-nums'>
                  {m.total_opens == null ? '—' : m.total_opens}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-white p-3'>
                <p className='text-[10px] font-semibold uppercase text-gray-500'>Conversions</p>
                <p className='text-lg font-semibold tabular-nums'>{m.conversions}</p>
              </div>
              <div
                className='rounded-lg border border-gray-200 bg-white p-3 cursor-help'
                title={CONV_PER_SEND_TOOLTIP}
              >
                <p className='text-[10px] font-semibold uppercase text-gray-500'>Conv. % of sends</p>
                <p className='text-lg font-semibold tabular-nums'>
                  {m.conversion_rate != null && m.total_sends > 0
                    ? `${(100 * m.conversion_rate).toFixed(1)}%`
                    : '—'}
                </p>
              </div>
            </div>

            {series.length > 0 && (
              <div className='mt-6'>
                <h3 className='text-sm font-semibold text-gray-900 mb-2'>Sends by day</h3>
                <svg
                  viewBox={`0 0 ${Math.max(series.length * 12, 120)} 48`}
                  className='w-full h-12 text-primary'
                  preserveAspectRatio='none'
                >
                  {series.map((pt, i) => {
                    const w = 10;
                    const x = i * 12 + 1;
                    const h = (pt.sends / maxS) * 40;
                    const y = 44 - h;
                    return <rect key={pt.date} x={x} y={y} width={w} height={h} fill='currentColor' rx={1} />;
                  })}
                </svg>
                <p className='text-[10px] text-gray-400 mt-1'>
                  Open rate:{' '}
                  {m.open_rate == null ? '—' : `${(100 * m.open_rate).toFixed(1)}%`} · Clicks are not persisted in
                  DB.
                </p>
              </div>
            )}

            <div className='mt-6 overflow-hidden rounded-lg border border-gray-200'>
              <div className='px-4 py-2 bg-gray-50 border-b border-gray-100'>
                <h3 className='text-sm font-semibold text-gray-900'>Steps</h3>
              </div>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-2'>Step</th>
                    <th className='px-4 py-2'>Channel</th>
                    <th className='px-4 py-2 text-right'>Sends</th>
                    <th className='px-4 py-2 text-right'>Opens</th>
                    <th className='px-4 py-2 text-right'>Open %</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {m.step_breakdown.map((row) => (
                    <tr key={`${row.step_key}-${row.channel}`} className='hover:bg-gray-50'>
                      <td className='px-4 py-2 font-mono text-xs'>{row.step_key}</td>
                      <td className='px-4 py-2'>{row.channel}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{row.sends}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{row.opens ?? '—'}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>
                        {row.opens == null ? '—' : pct(row.opens, row.sends)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        <div className='mt-8'>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Recent sends</h3>
          {sendsQuery.isLoading ? (
            <p className='text-gray-500 text-sm'>Loading…</p>
          ) : sendsQuery.isError ? (
            <p className='text-red-600 text-sm'>Could not load sends.</p>
          ) : (
            <div className='overflow-x-auto rounded-lg border border-gray-200'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-3 py-2'>When</th>
                    <th className='px-3 py-2'>Step</th>
                    <th className='px-3 py-2'>Recipient</th>
                    <th className='px-3 py-2'>Opened</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {(sendsQuery.data?.rows ?? []).map((r) => (
                    <tr key={r.id} className='hover:bg-gray-50'>
                      <td className='px-3 py-2 text-xs text-gray-600 whitespace-nowrap'>
                        {new Date(r.sent_at).toLocaleString()}
                      </td>
                      <td className='px-3 py-2 font-mono text-xs'>{r.step_key}</td>
                      <td className='px-3 py-2 max-w-[200px] truncate'>
                        {r.user_id != null && c?.keyed_by !== 'prospect_id' ? (
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(r.user_id) }}
                            search={{ hid: undefined }}
                            className='text-primary font-medium hover:underline'
                          >
                            {r.user_label?.trim() ? r.user_label : `User #${r.user_id}`}
                          </Link>
                        ) : r.invite_id != null ? (
                          <span title='SMS to invitee; inviter shown'>Invite #{r.invite_id}</span>
                        ) : r.prospect_id != null ? (
                          <span className='text-gray-700'>{r.user_label ?? `Prospect #${r.prospect_id}`}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className='px-3 py-2 text-xs'>
                        {r.opened_at ? (
                          <span className='text-green-700'>Yes</span>
                        ) : c?.tracking_enabled ? (
                          '—'
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
